"use client";

import { useCallback, useEffect, useState } from "react";
import Logo from "@/app/components/Logo";
import Button from "@/app/components/Button";
import type { QueueCharacter, QueueRequest, QueueView } from "@/lib/queue";

const WORLD = "Baunheim";
const REFRESH_MS = 30000;

const TYPE_LABEL: Record<string, string> = {
  new: "新規",
  maintain: "整備",
  fix: "修正",
  hold: "許可待ち",
};

const ROLE_LABEL: Record<string, string> = {
  pc: "PC",
  npc: "NPC",
  companion: "同行者",
  figure: "生い立ちの人物",
};

type QueueOp = "approve" | "reject" | "retry";

/** キャラの状態の札 */
function stateOf(row: QueueCharacter): string {
  if (row.dead) return "死亡";
  if (row.in_progress) return "進行中";
  if (row.in_battle) return "戦闘中";
  if (row.in_craft) return "作製中";
  if (row.in_rework) return "手直し中";
  if (row.in_recruit) return "味方キャラ作成中";
  if (row.waiting) return "進行待ち";
  return "入力待ち";
}

function stampOf(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getMonth() + 1}/${d.getDate()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** 依頼の数を種類ごとに並べた文 */
function countOf(rows: QueueRequest[]): string {
  const parts = Object.keys(TYPE_LABEL)
    .map((type) => [TYPE_LABEL[type], rows.filter((row) => row.type === type).length] as const)
    .filter(([, n]) => n > 0)
    .map(([label, n]) => `${label} ${n}`);
  return `依頼 ${rows.length} 件${parts.length > 0 ? `（${parts.join("・")}）` : ""}`;
}

function RequestRows({ rows, onHold, world }: { rows: QueueRequest[]; onHold?: (row: QueueRequest, op: QueueOp) => void; world?: boolean }) {
  if (rows.length === 0) return <p className="section-note">依頼はありません。</p>;
  return (
    <div className="slots">
      {rows.map((row) => (
        <div className="slot" key={`${row.type}/${row.id}`}>
          <div className="slot-body">
            <div className={row.name ? "slot-name" : "slot-name slot-name-empty"}>{row.name ?? "名の無い依頼"}</div>
            <div className="slot-meta">
              <span className="slot-state">{TYPE_LABEL[row.type] ?? row.type}</span>
              {row.approved ? " ／ 許可済み" : ""}
              {row.aborted !== null ? " ／ 中断" : ""}
              {row.witness ? " ／ 見直し" : ""}
              {row.role ? ` ／ ${ROLE_LABEL[row.role] ?? row.role}` : ""}
              {row.target ? ` ／ ${row.target}` : ""} ／ {row.account ?? "?"} ／ {stampOf(row.received_at)}
            </div>
            {row.hold_reason ? (
              <div className="slot-meta" style={{ whiteSpace: "pre-wrap" }}>
                {world ? "理由" : "及ぶ記録"}：{row.hold_reason}
              </div>
            ) : null}
            {row.aborted !== null ? (
              <div className="slot-meta" style={{ whiteSpace: "pre-wrap" }}>
                中断の趣旨：{row.aborted}
              </div>
            ) : null}
            {row.request ? (
              <div className="slot-meta" style={{ whiteSpace: "pre-wrap" }}>
                {row.request}
              </div>
            ) : null}
          </div>
          {row.type === "hold" && onHold ? (
            <div className="slot-actions">
              <Button variant="primary" inline onClick={() => onHold(row, "approve")}>
                {world ? "許可して出し直す" : "許可する"}
              </Button>
              <Button inline onClick={() => onHold(row, "reject")}>
                取り下げる
              </Button>
            </div>
          ) : null}
          {row.aborted !== null && !world && onHold ? (
            <div className="slot-actions">
              <Button variant="primary" inline onClick={() => onHold(row, "retry")}>
                出し直す
              </Button>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** Baunheim のリージョンごとの手直しの依頼と進行待ちを並べる管理者用画面 */
export default function QueueAdmin({ onBack }: { onBack: () => void }) {
  const [queue, setQueue] = useState<QueueView | null>(null);
  const [message, setMessage] = useState("読み込み中・・・");

  const load = useCallback(() => {
    fetch(`/api/admin/queue?world=${WORLD}`)
      .then(async (response) => {
        const body = await response.json();
        if (!response.ok) {
          setMessage(body.error ?? "一覧を読めません");
          return;
        }
        setQueue(body as QueueView);
        setMessage((previous) => (previous === "読み込み中・・・" ? "" : previous));
      })
      .catch(() => setMessage("サーバーに繋がりません"));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  /** 許可待ちの案を許可するか取り下げる。中断した依頼を出し直す */
  const hold = (region: string) => async (row: QueueRequest, op: QueueOp) => {
    const what =
      op === "retry"
        ? "の依頼を出し直します。次の整備で起こし直します"
        : region === ""
          ? op === "approve"
            ? "の依頼を、開発者の許可を付けて出し直します"
            : "の依頼を取り下げます"
          : op === "approve"
            ? "への直しを許可します。次の整備で、このキャラの組のハキリリが直します"
            : "への直しを取り下げます。元の依頼を出したキャラへお知らせが届きます";
    if (!window.confirm(`${row.name ?? row.id} ${what}。`)) return;
    setMessage("送っています");
    try {
      const response = await fetch("/api/admin/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ world: WORLD, region, id: row.id, op }),
      });
      const body = await response.json();
      setMessage(response.ok ? (op === "reject" ? "取り下げました" : op === "retry" ? "出し直しました" : region === "" ? "出し直しました" : "許可しました") : body.error ?? "送れません");
    } catch {
      setMessage("サーバーに繋がりません");
    }
    load();
  };

  return (
    <div className="panel panel-wide">
      <Logo />
      <div className="head">
        <span className="head-user">
          {WORLD}
          <strong>依頼と進行待ち</strong>
        </span>
        <Button onClick={onBack} inline>
          戻る
        </Button>
      </div>

      <div className="menu">
        <Button onClick={load} inline>
          読み直す
        </Button>
      </div>

      {queue?.regions.map((region) => {
        const waiting = region.characters.filter((row) => row.waiting);
        return (
          <section className="section" key={region.region}>
            <h2 className="section-title">
              {region.region}
              <span className="section-count">
                {countOf(region.requests)} ／ 進行待ち {waiting.length} 人
              </span>
            </h2>

            <h3 className="section-note">ハキリリへの依頼</h3>
            <RequestRows rows={region.requests} onHold={hold(region.region)} />

            <h3 className="section-note">キャラ</h3>
            <div className="slots">
              {region.characters.length === 0 ? <p className="section-note">キャラはいません。</p> : null}
              {region.characters.map((row) => (
                <div className="slot" key={row.slug}>
                  <div className="slot-body">
                    <div className="slot-name">{row.name}</div>
                    <div className="slot-meta">
                      {row.slug} ／ <span className="slot-state">{stateOf(row)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        );
      })}

      {queue ? (
        <section className="section">
          <h2 className="section-title">
            世界の直下
            <span className="section-count">{countOf(queue.world_requests)}</span>
          </h2>
          <RequestRows rows={queue.world_requests} onHold={hold("")} world />
        </section>
      ) : null}

      <p className="message message-info">{message}</p>
    </div>
  );
}
